import type { ShareableFieldValue } from '@amzn/journeys-common';
import { FormLabelWithOptional } from 'components/form/FormLabelWithOptional';
import { required } from 'components/form/form.util';
import { MFormInput } from 'components/MeridianFinalFormWrappers/MFormInput/MFormInput';
import type { ImageFormInputClassNames, ImageFormInputProps } from 'features/assets/ImageFormInput';
import { useSpyOnFieldValue } from 'hooks/form-spy/useSpyOnFieldValue';
import { useMemo } from 'react';
import { stylesheet } from 'typestyle';
import type { ShareableFieldConfigBase } from '../../../campaign-configs/types';
import { SharedVarWrapper } from '../../shared-variables/SharedVarWrapper';
import { FlavorImage } from '../FlavorImage';

export type LogoProps<TFieldValue extends ShareableFieldValue, TFlavorContent extends Record<string, unknown>> = {
  altTextConfig: {
    name: string;
    required?: boolean;
    shareableConfig: ShareableFieldConfigBase<TFieldValue, TFlavorContent>;
  };
  helperText?: string;
  label?: string;
  name: string;
  optional?: boolean;
  overrideClasses?: Partial<Record<ImageFormInputClassNames, string>>;
};

export const LOGO_IMAGE_CLASS: ImageFormInputProps['overrideClasses'] = stylesheet({
  selectImageButton: {
    maxHeight: '95px',
    maxWidth: '480px',
  },
});

export const Logo = <TFieldValue extends ShareableFieldValue, TFlavorContent extends Record<string, unknown>>({
  name,
  altTextConfig,
  helperText,
  label = 'Logo',
  optional = true,
  overrideClasses = LOGO_IMAGE_CLASS,
}: LogoProps<TFieldValue, TFlavorContent>) => {
  const logoValue = useSpyOnFieldValue({
    fieldPath: name,
  });

  const showAltText = useMemo(
    () => Boolean(logoValue),
    [
      logoValue,
    ],
  );

  return (
    <>
      <FormLabelWithOptional helperText={helperText} label={label} optional={optional} />
      <FlavorImage name={name} overrideClasses={overrideClasses} />
      {showAltText && (
        <SharedVarWrapper<TFieldValue, TFlavorContent>
          className="mb-3 mt-3"
          name={altTextConfig.name}
          shareableFieldConfig={altTextConfig.shareableConfig}
        >
          <MFormInput
            className="mb-0"
            label="Logo alt text *"
            name={`${altTextConfig.name}.value`}
            placeholder="Logo alt text"
            validate={altTextConfig.required ? required : undefined}
          />
        </SharedVarWrapper>
      )}
    </>
  );
};
